import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import CupsCifSelector from "../../CupsCifSelector/CupsCifSelector";
import theme from "../../../muiTheme";
import { useTranslation } from "react-i18next";
import DatePickersComponent from "../../UI/DatePickersComponent/DatePickersComponent";
import { RefObject, useContext, useState } from "react";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import csvImg from "../../../assets/images/csv.svg";
import xmlImg from "../../../assets/images/xml.svg";
import xlsImg from "../../../assets/images/xls.svg";
import "./ConsumptionsDownload.scss";
import { ManagementUpdateCup } from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import {
  DownloadFormatData,
  EnergyTypeGroupByData,
  GetConsumptionsFileRequestBody,
  SelectedDownloadData,
} from "../../../core/models/consumptions/consumptions.model";
import { addDayDate, formatDateString } from "../../../utils/dateUtils/formatDate.util";
import { getConsumptionsFile } from "../../../core/services/consumptions/consumptions.service";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import SnackbarComponent from "../../UI/SnackbarComponent/SnackbarComponent";
import { clearFirstLoadCifCups } from "../../../utils/components/cupsCifSelector/cupsCifSelector.util";
import { initialSelectedDowloadData } from "../../../utils/components/consumptions/consumptions.util";

interface ConsumptionsDonwloadProps {
  selectedDownloadCups: ManagementUpdateCup[];
  setSelectedDownloadCups: React.Dispatch<
    React.SetStateAction<ManagementUpdateCup[]>
  >;
  setScrollPosition: React.Dispatch<React.SetStateAction<number | null>>;
  selectedDownloadData: SelectedDownloadData;
  setSelectedDownloadData: React.Dispatch<
    React.SetStateAction<SelectedDownloadData>
  >;
  tableElementRef: RefObject<HTMLDivElement>;
}

const ConsumptionsDonwload = ({
  selectedDownloadCups,
  setSelectedDownloadCups,
  setScrollPosition,
  selectedDownloadData,
  setSelectedDownloadData,
  tableElementRef,
}: ConsumptionsDonwloadProps) => {
  const {
    userData,
    snackbarData,
    setSnackbarData,
    cifCupsState,
    storageCifCupsState,
  } = useContext(TotalEnergiesContext);
  const { t } = useTranslation();
  const breakpoints = { md: 12, lg: 11 };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isButtonDisabled =
    Object.values(selectedDownloadData).some(
      (selectedDownloadDataValue) =>
        !selectedDownloadDataValue ||
        ((typeof selectedDownloadDataValue === "string" ||
          Array.isArray(selectedDownloadDataValue)) &&
          !selectedDownloadDataValue.length)
    ) || !selectedDownloadCups.length;

  const { dateFrom, dateTo, groupBy, energyType, fileType } =
    selectedDownloadData;

  const energyTypeData: EnergyTypeGroupByData[] = [
    { id: "active", label: "Activa", value: "00" },
    { id: "reactive", label: "Reactiva", value: "01" },
    // { id: "capacitive", label: "Capacitiva", value: "02" },
  ];

  const groupByData: EnergyTypeGroupByData[] = [
    { id: "year", label: "Año", value: "00" },
    { id: "month", label: "Mes", value: "01" },
    { id: "day", label: "Día", value: "02" },
    { id: "hour", label: "Hora", value: "03" },
    { id: "quarter", label: "Cuarto de hora", value: "04" },
  ];

  const downloadFormatData: DownloadFormatData[] = [
    {
      id: "excel",
      label: "Excel",
      value: "01",
      altText: "icon-excel",
      icon: xlsImg,
    },
    {
      id: "csv",
      label: "CSV",
      value: "00",
      altText: "icon-csv",
      icon: csvImg,
    },
    {
      id: "xml",
      label: "XML",
      value: "02",
      altText: "icon-xml",
      icon: xmlImg,
    },
  ];

  const handleClickDownload = async () => {
    const getConsumptionsFileRequestBody: GetConsumptionsFileRequestBody = {
      cupsId: selectedDownloadCups.map(({ cupsId }) => cupsId.toString()),
      Cups: selectedDownloadCups.map(({ cupsCode }) => cupsCode),
      CodigosContrato: selectedDownloadCups.map(({ cupsContractCode }) =>
        cupsContractCode.toString()
      ),
      dateFrom: formatDateString(addDayDate(dateFrom?.$d as Date)).replace(",", ""),
      dateTo: formatDateString(addDayDate(dateTo?.$d as Date)).replace(",", ""),
      groupBy,
      fileType,
      energyType,
    };

    setIsLoading(true);
    await getConsumptionsFile(
      getConsumptionsFileRequestBody,
      userData?.token || "",
      () => {
        setSnackbarData({
          isOpen: true,
          isSuccessSeverity: true,
          text: "Archivo guardado en la carpeta de descargas",
        });
      }
    )
      .then(() => {
        setSnackbarData({
          isOpen: true,
          isSuccessSeverity: true,
          text: "Descarga realizada con éxito",
        });
        storageCifCupsState(clearFirstLoadCifCups(cifCupsState.listCupsCifRaw));
        setSelectedDownloadData(initialSelectedDowloadData);
        setSelectedDownloadCups([]);
      })
      .catch((e) => {
        if (e.response?.status === 404) {
          setSnackbarData({
            isOpen: true,
            isSuccessSeverity: false,
            text: `No hay datos para descargar`,
          });
        } else {
          setSnackbarData({
            isOpen: true,
            isSuccessSeverity: false,
            text: `Ha habido un error con la descarga. Inténtalo de nuevo con un periodo más reducido`,
          });
        }
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Stack
      component="article"
      marginTop={5}
      marginBottom={10}
      bgcolor={theme.palette.background.default}
      borderRadius="12px"
      boxShadow="0px 3px 5px 0px rgba(55, 70, 73, 0.20)"
    >
      <CupsCifSelector
        setUpdateCups={() => {}}
        title={"Header.Nav.Suministros"}
        description={t(
          "Consumos.Selecciona uno o más suministros para descargar los datos de consumo"
        )}
        selectedCups={selectedDownloadCups}
        setSelectedCups={setSelectedDownloadCups}
        breakpoints={breakpoints}
        isManagementsComponent={false}
        bgDark={true}
        tableElementRef={tableElementRef}
        setScrollPosition={setScrollPosition}
      />
      <Stack gap="3rem" padding="3rem">
        <Box component={"div"}>
          <Typography component="h3" variant="h3" paddingBottom="1.5rem">
            {t("Consumos.Periodo")}
          </Typography>
          <DatePickersComponent
            billsSearchBar
            filters={{ dateFrom, dateTo }}
            setFilters={(id, value) =>
              setSelectedDownloadData((prevDateSelection) => ({
                ...prevDateSelection,
                [id]: value,
              }))
            }
          />
        </Box>
        <Box component={"div"}>
          <Typography component="h3" variant="h3" paddingBottom="1.5rem">
            {t("Consumos.Tipo de energía")}
          </Typography>
          <Box
            display={"flex"}
            gap={"1.5rem"}
            flexDirection={{ xs: "column", md: "row" }}
          >
            {energyTypeData.map(({ id, label, value }) => (
              <Box key={id} component={"div"} display={"flex"} fontSize="24px">
                <Checkbox
                  icon={<span className="t-icon-checkbox-default"></span>}
                  checked={energyType.includes(value)}
                  checkedIcon={
                    <span className="t-icon t-icon-checkbox-checked"></span>
                  }
                  onChange={({ target: { checked } }) =>
                    setSelectedDownloadData((prevSelectedDownloadData) => ({
                      ...prevSelectedDownloadData,
                      energyType: checked
                        ? [...energyType, value]
                        : energyType.filter((type) => type !== value),
                    }))
                  }
                />
                <Typography
                  component={"p"}
                  variant="subtitle2"
                  paddingLeft="0.5rem"
                  color={theme.palette.grey[700]}
                >
                  {t(`Consumos.${label}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box component={"div"}>
          <Typography component="h3" variant="h3" paddingBottom="1.5rem">
            {t("Consumos.Agrupar")}
          </Typography>
          <RadioGroup
            sx={{ display: "flex", flexDirection: "row", gap: "1rem" }}
            value={groupBy}
            className="consumption-radio"
          >
            {groupByData.map(({ id, label, value }) => (
              <FormControlLabel
                key={id}
                value={value}
                label={t(`Consumos.${label}`)}
                onChange={() =>
                  setSelectedDownloadData((prevSelectedDownloadData) => ({
                    ...prevSelectedDownloadData,
                    groupBy: groupBy === value ? "" : value,
                  }))
                }
                control={<Radio disableRipple />}
              />
            ))}
          </RadioGroup>
        </Box>
        <Box component={"div"}>
          <Typography component="h3" variant="h3" paddingBottom="1.5rem">
            {t("Consumos.Formato de descarga")}
          </Typography>
          <Box
            component={"div"}
            display={"flex"}
            gap={"2rem"}
            flexDirection={{ xs: "column", md: "row" }}
          >
            {downloadFormatData.map(({ id, label, altText, value, icon }) => (
              <Box
                key={id}
                component={"div"}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                padding="1rem"
                borderRadius="4px"
                border="1px solid"
                bgcolor={fileType === value ? theme.palette.selected.light : ""}
                borderColor={
                  fileType === value
                    ? theme.palette.selected.main
                    : theme.palette.grey[300]
                }
                width={{ xs: "90%", md: "30%" }}
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  setSelectedDownloadData((prevSelectedDownloadData) => ({
                    ...prevSelectedDownloadData,
                    fileType: fileType === value ? "" : value,
                  }))
                }
              >
                <Typography
                  component={"span"}
                  variant={"body1"}
                  display={"flex"}
                >
                  <img
                    src={icon}
                    alt={altText}
                    style={{ paddingRight: "1rem" }}
                  />{" "}
                  {label}
                </Typography>
                {fileType === value && (
                  <span
                    className="t-icon-check"
                    style={{
                      color: "white",
                      background: theme.palette.selected.main,
                      borderRadius: "100px",
                      padding: "4px",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Stack>
      <Stack
        width={{ xs: "80%", md: "20%" }}
        paddingX={{ xs: "10%", md: "2.5rem" }}
        paddingBottom="1.5rem"
      >
        <ButtonElement
          disabled={isButtonDisabled}
          onClick={() => handleClickDownload()}
          isLoading={isLoading}
        >
          {t("Consumos.Descargar")}
        </ButtonElement>
      </Stack>
      <SnackbarComponent
        snackbarData={snackbarData}
        setSnackbarData={setSnackbarData}
      />
    </Stack>
  );
};

export default ConsumptionsDonwload;
